"t";
import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  Snackbar,
  Alert,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import { IndiaFlag, USFlag } from "../constant";
import CustomGlowButton from "./GlowingButton";
import axios from "axios";
import UnsubscribeModal from "./UnsubscribeModel";

const MainContainer = styled(Box)(({ theme }) => ({
  background: "#000",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "60px 24px",
  position: "relative",
  overflow: "hidden",

  "&::after": {
    content: '""',
    position: "absolute",
    width: "95%",
    height: "80%",
    left: "65%",
    top: '10%',
    bottom: "10%",
    transform: "translateX(-100%), translateY(-50%)",
    background: "radial-gradient(circle, rgba(99, 102, 241, 0.65) 0%, rgba(99, 102, 241, 0) 70%)",
    filter: "blur(100px)",
    backdropFilter: 'blur(500px)',
    zIndex: 0,
  },
  [theme.breakpoints.down("sm")]: {
    padding: "0px 24px",
  }


}));

const ContentWrapper = styled(Box)(({ theme }) => ({
  width: "100%",
  maxWidth: "1200px",
  margin: "0 auto",
  position: "relative",
  zIndex: 1,
  [theme.breakpoints.down("md")]: {
    width: "95%",
  },
  [theme.breakpoints.down("sm")]: {
    width: "100%",
  }
}));

const GlassBox = styled(Box)(({ theme }) => ({
  width: "40%",
  position: "absolute",
  top: "50%",
  right: theme.spacing(6),
  transform: "translateY(-50%)",
  padding: theme.spacing(4),
  borderRadius: "24px",
  background: "linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.03) 100%)",
  backdropFilter: "blur(20px)",
  border: "1px solid rgba(255, 255, 255, 0.1)",
  boxShadow: "0 4px 24px rgba(0, 0, 0, 0.1)",
  zIndex: 2,

  [theme.breakpoints.down("md")]: {
    width: "100%",
    maxWidth: "500px",
    position: "relative",
    right: "auto",
    top: "auto",
    transform: "none",
    margin: theme.spacing(5, "auto", 0),
  },

  [theme.breakpoints.down("sm")]: {
    width: "100%", // Make it 90% to prevent overflow
    position: "relative",
    padding: theme.spacing(4),
    margin: theme.spacing(5, "auto"), // Center properly
    left: "auto",
    right: "auto",
    transform: "none",
    textAlign: "center", // Ensures content inside is centered
  }
}));


const OuterGlassBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: "24px",
  background: "radial-gradient(90.16% 143.01% at 15.32% 21.04%, rgba(165, 239, 255, 0.2) 0%, rgba(110, 191, 244, 0.0447917) 77.08%, rgba(70, 144, 213, 0) 100%)",
  backdropFilter: "blur(10px)",
  border: "1px solid rgba(255, 255, 255, 0.1)",
  position: "relative",
  overflow: "hidden",
  minHeight: "600px",
  boxShadow: "0 4px 24px rgba(0, 0, 0, 0.1)",
  width: "100%", // Added to ensure it takes full width
  margin: "0 auto", // Center the outer box

  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(2),
    minHeight: "auto", // Allow it to adapt to content on smaller screens
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(4),
    margin: theme.spacing(2, "auto"),
  }
}));

const GradientTitle = styled(Typography)(({ theme }) => ({
  fontSize: "48px",
  fontWeight: 700,
  lineHeight: 1.2,
  background: "linear-gradient(135deg, #6366F1 0%, #3B82F6 100%)",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  marginBottom: theme.spacing(3),
  wordBreak: "break-word", // Ensures words break properly
  whiteSpace: "normal", // Allows text to wrap to the next line

  [theme.breakpoints.down("md")]: {
    fontSize: "36px",
  }
}));


const CustomTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    color: "#fff",
    backgroundColor: "rgba(255, 255, 255, 0.03)",
    borderRadius: "12px",
    height: "56px",

    "& fieldset": {
      borderColor: "rgba(255, 255, 255, 0.1)",
      borderWidth: "1px",
    },
    "&:hover fieldset": {
      borderColor: "rgba(255, 255, 255, 0.2)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#6366f1",
    },
  },
  "& .MuiInputBase-input": {
    "&::placeholder": {
      color: "#8C8CC2",
      opacity: 1,
    },
  },
   "& input:-webkit-autofill": {
      WebkitBoxShadow: "0 0 0 100px rgba(255,255,255,0.03) inset",
      WebkitTextFillColor: "#fff",
      caretColor: "#fff",
      borderRadius: "12px",
      transition: "background-color 5000s ease-in-out 0s",
    },

    "& input:-webkit-autofill:hover": {
      WebkitBoxShadow: "0 0 0 100px rgba(255,255,255,0.03) inset",
      WebkitTextFillColor: "#fff",
    },

    "& input:-webkit-autofill:focus": {
      WebkitBoxShadow: "0 0 0 100px rgba(255,255,255,0.03) inset",
      WebkitTextFillColor: "#fff",
    },
}));

const ContactUs = () => {
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: ""
  });

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({
      ...prev,
      open: false,
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const payload = {
        name: formData?.fullName,
        email: formData?.email,
        phone: formData?.phone
      };
      const result = await axios.post(
        "https://dev.ienergydigital.com/task-api/api/email-suits/contact-us",
        payload
      );

      if (result.data?.success) {
        setSnackbar({
          open: true,
          message:
            "Your demo request has been submitted successfully. Our team will contact you shortly.",
          severity: "success",
        });
      }

      setFormData({
        phone: "",
        fullName: "",
        email: "",
      });

    } catch (error) {
      setFormData({
        phone: "",
        fullName: "",
        email: "",
      });
      setSnackbar({
        open: true,
        message:
          error?.response?.data?.message || 
          "Something went wrong. Please try again later.",
        severity: "error",
      });
    }
  };
  return (
    <MainContainer>
      <ContentWrapper>
        <OuterGlassBox>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Box sx={{ p: { xs: 0, md: 4 } }}>
                <GradientTitle>
                  Let’s Build Safer, Smarter Operations Together
                </GradientTitle>
                <Typography
                  variant="body1"
                  sx={{
                    color: "rgba(255, 255, 255, 0.8)",
                    fontSize: "18px",
                    maxWidth: "480px",
                    mb: 6
                  }}
                >
                  Partner with a team focused on execution, accountability, and scalable solutions built for complex industrial environments.
                </Typography>
                
              </Box>
            </Grid>
          </Grid>
        </OuterGlassBox>

        <GlassBox>
          <Typography
            variant="h5"
            sx={{
              color: "#fff",
              fontWeight: 700,
              textAlign: "center",
              mb: 1,
              lineHeight: '34px'
            }}
          >
            Contact us
          </Typography>
          <Typography
            sx={{
              color: "#8C8CC2",
              textAlign: "center",
              mb: 4,
              fontSize: "14px"
            }}
          >
            Tell us about your operational priorities to identify the right approach forward.
          </Typography>

          <Box component="form" sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            <CustomTextField
              fullWidth
              placeholder="Full Name*"
              name="fullName"
              value={formData?.fullName}
              onChange={handleChange}
              required
              InputProps={{
                endAdornment: <PersonIcon sx={{ color: "#6366f1" }} />
              }}
            />
            <CustomTextField
              fullWidth
              placeholder="Mobile No.*"
              name="phone"
              value={formData?.phone}
              onChange={handleChange}
              required
              InputProps={{
                endAdornment: <PhoneIcon sx={{ color: "#6366f1" }} />
              }}
            />
            <CustomTextField
              fullWidth
              placeholder="Email Address*"
              name="email"
              value={formData?.email}
              onChange={handleChange}
              required
              InputProps={{
                endAdornment: <EmailIcon sx={{ color: "#6366f1" }} />
              }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  sx={{
                    color: "#8C8CC2",
                    "&.Mui-checked": { color: "#6366f1" },
                  }}
                />
              }
              label={
                <Typography sx={{ color: "#8C8CC2", fontSize: "14px" }}>
                  *I have read the{" "}
                  <Box component="span" sx={{ color: "#6366f1", textDecoration: "underline" }}>
                    Privacy Policy
                  </Box>{" "}
                  and agree to its terms.
                </Typography>
              }
            />

            <CustomGlowButton onClick={handleSubmit} text={`Schedule a Consultation`} />
          </Box>
        </GlassBox>
      </ContentWrapper>

      <Snackbar
        open={snackbar?.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar?.severity}
          variant="filled"
          sx={{
            width: "100%",
            borderRadius: "12px",
            fontWeight: 600,
            fontSize: "14px",
            boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
          }}
        >
          {snackbar?.message}
        </Alert>
      </Snackbar>
    </MainContainer>
  );
};

export default ContactUs;